import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { sevColor, sevName } from '../../theme';

interface Props<T extends string> {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; sev: number | null; label: string }[];
  size?: 'small' | 'medium';
}

/**
 * Pill-styled severity dropdown — matches the table badge colours.
 * The closed Select is filled with the selected severity colour; open
 * options each carry their severity colour.
 */
export function SeveritySelect<T extends string>({ value, onChange, options, size = 'small' }: Props<T>) {
  const current = options.find((o) => o.value === value);
  const fill = current?.sev != null ? sevColor(current.sev) : undefined;
  return (
    <Select
      size={size}
      value={value}
      onChange={(e: SelectChangeEvent<T>) => onChange(e.target.value as T)}
      sx={{
        minWidth: 110,
        borderRadius: 999,
        fontWeight: 700,
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        ...(fill && {
          color: '#fff',
          bgcolor: fill,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: fill },
          '& .MuiSelect-icon': { color: '#fff' },
        }),
      }}
    >
      {options.map((o) => (
        <MenuItem
          key={o.value}
          value={o.value}
          sx={o.sev != null ? {
            bgcolor: sevColor(o.sev),
            color: '#fff',
            fontWeight: 700,
            '&:hover': { bgcolor: sevColor(o.sev), filter: 'brightness(1.1)' },
            '&.Mui-selected': { bgcolor: sevColor(o.sev), filter: 'brightness(0.95)' },
          } : undefined}
        >
          {o.sev != null ? sevName(o.sev) : o.label}
        </MenuItem>
      ))}
    </Select>
  );
}
